// Core packages
import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { FormGroup } from "@angular/forms";

// Third party packages
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material";
import { FormlyFieldConfig, FormlyFormOptions } from "@ngx-formly/core";
import { TranslateService } from "@ngx-translate/core";
import { MatProgressButtonOptions } from "mat-progress-buttons";
import { CookieService } from "ngx-cookie-service";
import { ToastrService } from "ngx-toastr";
import { Subscription } from "rxjs";
import { finalize, tap } from "rxjs/operators";

// Custom packages
import { AuthService } from "src/app/modules/auth/auth.service";
import { UsersService } from 'src/app/modules/users/users.service';
import { PrintQrDialogComponent } from 'src/app/shared/components/dialogs/print-qr-dialog/print-qr-dialog.component';
import { IUser } from "src/app/shared/models/user/user.interface";
import { ConfigService } from "src/app/shared/services/config.service";
import { HelperService } from "src/app/shared/services/helper.service";
import { DocumentArchivesService } from "../../document-archives.service";
import { PropertiesService } from '../../properties.service';

/**
 * Script start
 */

@Component({
  selector: "app-document-paper-insert-dialog",
  templateUrl: "./document-paper-insert-dialog.component.html",
  styleUrls: ["./document-paper-insert-dialog.component.scss"]
})
export class DocumentPaperInsertDialogComponent
  implements OnInit, OnDestroy {
  private subscriptions: Subscription[] = [];
  loggedUser: IUser;
  loggedUserPermissions: string[];
  lang = this.translateService.currentLang || this.translateService.defaultLang;
  dialogIsLoading = false; // Used to view/hide form loading spinner

  // BEGIN - Edit item form
  // BEGIN - ngx-formly
  form: FormGroup = new FormGroup({});
  model: any = {};
  fields: FormlyFieldConfig[] = [];
  options: FormlyFormOptions = {};
  // END - ngx-formly
  formBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.save"),
    spinnerSize: 19,
    raised: true,
    stroked: true,
    flat: false,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-save",
      inline: true
    }
  };
  printModuleBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.printModule"),
    spinnerSize: 19,
    raised: false,
    stroked: false,
    flat: true,
    fab: false,
    buttonColor: "primary",
    spinnerColor: "primary",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-print",
      inline: true
    }
  };
  printQrBtnOptions: MatProgressButtonOptions = {
    active: false,
    text: this.translateService.instant("common.cta.printLabel"),
    spinnerSize: 19,
    raised: false,
    stroked: false,
    flat: true,
    fab: false,
    buttonColor: "accent",
    spinnerColor: "accent",
    fullWidth: false,
    disabled: false,
    mode: "indeterminate",
    buttonIcon: {
      fontSet: "fal",
      fontIcon: "fa-qrcode",
      inline: true
    }
  };
  // END - Edit item form
  existingNotes: any[] = [];
  barCode = "";
  qrcodeData = "";
  createdAt = "";
  createdBy = "";
  contract = false;
  expired = false;
  pdfMsgUrl: any[] = [];
  previousFileValue: any;
  userDataSetted = false;
  userData = {
    createdAt: "",
    createdBy: {
      _id: "",
      firstName: "",
      lastName: "",
    },
    // updatedAt: "",
    // updatedBy: {
    //   _id: "",
    //   firstName: "",
    //   lastName: "",
    // },
  }
  docType: string;
  docUploaded = false;
  docsSelected: any;

  structureSelected: any[] = [];

  /**
   * Class constructor
   */
  constructor(
    public dialogRef: MatDialogRef<DocumentPaperInsertDialogComponent>,
    public cancelContractDialog: MatDialog,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private configService: ConfigService,
    private propertiesService: PropertiesService,
    private translateService: TranslateService,
    private authService: AuthService,
    private cookieService: CookieService,
    private dialog: MatDialog,
    private toastrService: ToastrService,
    private helperService: HelperService,
    private documentArchivesService: DocumentArchivesService,
    private usersService: UsersService,
  ) { }

  /**
   * Init components
   *
   * @since 1.0.0
   */
  ngOnInit(): void {
    // Get logged user data
    this.subscriptions.push(
      this.authService.loggedUser$.subscribe(res => (this.loggedUser = res))
    );
    this.subscriptions.push(
      this.authService.loggedUserPermissions$.subscribe(
        res => (this.loggedUserPermissions = res)
      )
    );

    // Get current lang
    this.lang = this.cookieService.get("lang") || this.configService.defaultLang;

    this.barCode = this.data.barCode;

  }

  /**
   * Handle component destroy
   *
   * @since 1.0.0
   */
  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  /**
   * Init form basing on docType
   *
   * @since 1.0.0
   */
  onChooseDocType(type: string): void {
    this.initForm(type);
    this.docType = type;

    setTimeout(() => {
      if (
        this.docType === 'digital'
        && (this.data.action === 'new' || this.data.action === 'edit')
      ) {
        this.watchFileChanges();
      }
    }, 500);
  }

  /**
   * Init form
   *
   * @since 1.0.0
   */
  initForm(docType?: string): void {
    if (this.data) {
      // Check if this document is a contract (if not let's hide cancellation button)
      if (this.data.contract) {
        this.contract = this.data.contract;
      }
      if (this.data.expired) {
        this.expired = this.data.expired;
      }
      if (this.data.contractPdfMsg && Array.isArray(this.data.contractPdfMsg)) {
        this.data.contractPdfMsg.forEach((item: any) => {
          const path = item.msg.replace(/src[\\\/]assets/g, 'public');
          let url = "";
          // if (path.substring(0,22) === "https://app.archedi.it") {
          //   url = path;
          // } else {
          //   url = `${this.configService.apiHost}/${path}`;
          // }
          url = `${this.configService.apiHost}/${path}`;
          this.pdfMsgUrl.push({
            url: url,
            sent: item.sent
          });
        })
      }
      if (this.data.itemData) {
        this.model = this.data.itemData;

        if (this.data.itemData.note) {
          this.existingNotes = this.data.itemData.note;
          this.data.itemData.note = null;
        }

        // Get user name
        if (this.data.itemData.createdBy) {
          this.usersService.get(this.data.itemData.createdBy).subscribe(
            res => {
              if (res.status) {
                this.userData = {
                  createdAt: this.data.itemData.createdAt,
                  createdBy: {
                    _id: this.data.itemData.createdBy,
                    firstName: res.data.firstName,
                    lastName: res.data.lastName,
                  },
                  // updatedAt: "",
                  // updatedBy: {
                  //   _id: "",
                  //   firstName: "",
                  //   lastName: "",
                  // },
                }
                this.userDataSetted = true;
              }
            });
        }

      } else {
        this.contract = false;
      }

      if (this.data.itemConfig && this.data.itemConfig) {
        this.fields = this.data.itemConfig;
        if (docType && docType === 'digital') {
          this.fields.push(
            {
              fieldGroupClassName: "row",
              fieldGroup: [
                {
                  className: "col-12",
                  type: "file-uploader",
                  key: "documentFile",
                  templateOptions: {
                    required: true,
                    allowedFilesNumber: 200,
                    allowedFilesExtensions: this.configService.allowedDocumentExtensions,
                    customLabel: this.translateService.instant('properties.detail.documentArchive.fields.file'),
                  },
                  // Show file upload only if file is not uploaded yet
                  hideExpression:
                    typeof this.data.itemData !== 'undefined'
                    && this.data.itemData.document
                    && this.data.itemData.document._id !== ''
                }
              ]
            }
          );
        } else if (docType && docType === 'paper') {
          // For better fields displaying
          this.dialogRef.updateSize('60vw');
          this.fields.push(
            {
              fieldGroupClassName: "row",
              fieldGroup: [
                {
                  className: "col-6",
                  type: "input",
                  key: "numFiles",
                  hooks: {
                    onInit: (field: any): void => {
                      field.formControl.valueChanges
                        .pipe(
                          tap(value => {
                            if (typeof value === "undefined") {
                              value = null;
                            }
                            if (value > field.templateOptions.max) {
                              value = field.templateOptions.max;
                            }
                            if (this.model.files) {
                              this.model.files.length = value;
                            }
                            this.model = {
                              ...this.model,
                              numFiles: value
                            };
                          })
                        )
                        .subscribe();
                    }
                  },
                  templateOptions: {
                    type: "number",
                    label: this.translateService.instant('properties.detail.documentArchive.fields.numFiles'),
                    required: false,
                    min: 2,
                    max: 100,
                  },
                  expressionProperties: {
                    // apply expressionProperty for disabled based on formState
                    "templateOptions.disabled": "formState.disabled"
                  },
                },
                {
                  className: "docs-radio-group-wrapper col-12",
                  key: "files",
                  type: "repeat",
                  expressionProperties: {
                    // apply expressionProperty for disabled based on formState
                    "templateOptions.disabled": "formState.disabled"
                  },
                  fieldArray: {
                    fieldGroup: [
                      {
                        className: "pl-0 col-12 col-md-6",
                        type: "input",
                        key: "name",
                        hooks: {
                          onInit(field) {
                            const type = field.parent.fieldGroup[1].defaultValue[0]; // take first letter from radio's defaultValue
                            const name = this.barCode; // barcode value
                            const index = +field.parent.key + 1; // actual array index plus 1
                            field.defaultValue = `${name}_${type}${index}`.toUpperCase();
                          },
                        },
                        templateOptions: {
                          label: this.translateService.instant('properties.detail.documentArchive.fields.name'),
                          placeholder: this.translateService.instant('properties.detail.documentArchive.fields.name'),
                          minLength: 0,
                          maxLength: 500,
                          readonly: true,
                        },
                      },
                      {
                        className: "col-12 col-md-6",
                        type: "radio",
                        key: "docType",
                        defaultValue: "report",
                        expressionProperties: {
                          "templateOptions.onChange": (model: any, opt: any, field: any) => {
                            if (model) {
                              const position = +field.parent.key;

                              // @todo: da rivedere (segnalato in #105)
                              // Return if no changes were made
                              // if (
                              //   this.previousDocTypes.map(el => el.value).includes(field.formControl.value)
                              //   && this.previousDocTypes.map(el => el.position).includes(position)
                              // ) {
                              //   return;
                              // }
                              // this.previousDocTypes = [];
                              // field.parent.parent.fieldGroup.forEach(el => {
                              //   this.previousDocTypes.push({
                              //     position,
                              //     value: el.formControl.value.docType
                              //   });
                              // });

                              const type = model.docType[0]; // take first letter from radio's actual value
                              const name = this.barCode; // barcode value
                              const index = position + 1; // actual array index plus 1;

                              // Set new value in model
                              model.name = `${name}_${type}${index}`.toUpperCase();

                              // Set new value in field (make it change inside form input)
                              field.parent.fieldGroup[0].formControl.setValue(model.name);
                            }
                          },
                        },
                        templateOptions: {
                          label: this.translateService.instant('properties.detail.documentArchive.fields.docType'),
                          options: [
                            {
                              value: "report",
                              label: this.translateService.instant('properties.detail.documentArchive.fields.report'),
                            },
                            {
                              value: "tables",
                              label: this.translateService.instant('properties.detail.documentArchive.fields.tables'),
                            },
                            {
                              value: "calcs",
                              label: this.translateService.instant('properties.detail.documentArchive.fields.calcs'),
                            },
                            {
                              value: "schemas",
                              label: this.translateService.instant('properties.detail.documentArchive.fields.schemas'),
                            },
                            {
                              value: "various",
                              label: this.translateService.instant('properties.detail.documentArchive.fields.various'),
                            }
                          ],
                        }
                      },
                    ]
                  }
                }
              ]
            }
          );
        } else {
          this.toastrService.error('Error: no docType selected');
        }
      }

      // QRcode.
      // Attention: not per "contratti"
      const section = this.data.documentTypeRoute;
      const host = this.configService.frontEndHost;
      const documentId = this.data.documentId ? this.data.documentId : 'generic';
      if (section !== "locazione-contrdi-locazione"
        && section !== "contratti" && !this.contract) {
        this.qrcodeData =
          `${host}/qrcode/${this.data.documentArchiveId}/${section}/${documentId}/${this.data.documentKey}/${this.data.subDocumentId}`;
        // this.qrcodeData = JSON.stringify({
        //   archive: this.data.documentArchiveId,
        //   section,
        //   doc: this.data.documentId,
        //   key: this.data.documentKey,
        //   subDoc: this.data.subDocumentId,
        // });
      }
    }
  }

  /**
   * Watch for file-uploader changes, if file was uploaded
   * scroll down on dialog bottom
   *
   * @since 1.0.0
   */
  watchFileChanges(): void {
    this.subscriptions.push(
      this.form.controls["documentFile"].valueChanges.subscribe(
        res => {
          // A file was uploaded, scroll down dialog
          if (Array.isArray(res) && res.length) {
            const dialogWindow = document.getElementsByTagName("mat-dialog-container")[0]; // assuming we can have only one dialog opened
            if (dialogWindow) {
              const contentToScroll = dialogWindow.getElementsByClassName("mat-dialog-content");
              if (contentToScroll[0]) {
                const bottomHeight = contentToScroll[0].scrollHeight;
                contentToScroll[0].scrollTo({
                  top: bottomHeight,
                  behavior: "smooth",
                });
              }
            }
          }
        },
        err => {
          this.helperService.handleError(err);
        }
      ));
  }

  /**
   * Handle dialog closing
   *
   * @since 1.0.0
   */
  onClose(data?: any): void {
    this.dialogRef.close(data);
  }

  /**
   * Handle form submit
   *
   * @since 1.0.0
   */
  onFormSubmit(): void {
    // Prevent invalid form submit
    if (this.form.invalid) {
      Object.keys(this.form.controls).forEach(key => {
        this.form.controls[key].markAsTouched();
      });
      console.warn("form", this.form);
      this.toastrService.error(
        this.translateService.instant("common.errors.form_is_invalid")
      );
      // Set errors inside invalid form controls
      Object.keys(this.form.controls).forEach(key => {
        const control = this.form.controls[key];
        if (control.invalid) {
          control.markAsTouched();
          control.setErrors({
            required: true,
          });
        }
      });
      return;
    }

    // Activate submit button loading status
    this.formBtnOptions.active = true;

    if (this.form.value) {
      if (this.data.action && this.data.action === "new") {
        if (this.data.structureId) {
          let structureMatched = false;
          if (
            this.data.itemData &&
            this.data.itemData.structureSelected &&
            Array.isArray(this.data.itemData.structureSelected)
          ) {
            this.data.itemData.structureSelected.forEach((el: any) => {
              if (this.data.structureId.toString() === el.id.toString()) {
                this.structureSelected.push({
                  id: el.id,
                  name: el.name,
                  type: el.type,
                  typeStructure: el.typeStructure,
                  value: true,
                });
                structureMatched = true;
              }
            });
          }
          // // itemData is empty when inserting a brand new document (no data yet),
          // // so structureSelected options built from it are unavailable: fall back
          // // to structureId alone to still tag the document with its structure.
          // if (!structureMatched) {
          //   this.structureSelected.push({
          //     id: this.data.structureId,
          //     value: true,
          //   });
          // }
        }
        this.docsSelected = [{
          subDoc: this.data.keyTranslateTitle,
          subAreaTitle: this.data.topic ? this.data.topic : '',
          title: this.data.modalTitle.trim(),
          key: this.data.documentKey,
          structureSelected: this.structureSelected.length > 0 ? this.structureSelected : undefined,
        }];
        // let structureSelected: any[];
        // if (this.data.structureId) {
        //   structureSelected = [{
        //     id: this.data.structureId,
        //     value: false,
        //   }];
        // }
        
        if (this.docType && this.docType === 'digital') {
          let structureSelected = this.structureSelected.length > 0 ? this.structureSelected : undefined;
          // check contracts section
          const section = this.data.documentTypeRoute;
          if (section === 'contratti') {
            if (typeof structureSelected === "undefined") {
              structureSelected = [];
            }
            if (
              this.docsSelected && 
              Array.isArray(this.docsSelected) &&
              this.docsSelected.length > 0 &&
              (
                !this.docsSelected[0].structureSelected ||
                typeof this.docsSelected[0].structureSelected === "undefined" ||
                !Array.isArray(this.docsSelected[0].structureSelected)
              )
            ) {
              this.docsSelected[0].structureSelected = [];
            }
          }

          let isContract = false;
          if (
            this.data.documentKey === "HA6" ||
            this.data.documentKey === "HA7" ||
            this.data.documentKey === "HA8" ||
            this.data.documentKey === "HA12" ||
            this.data.documentKey === "HA64" ||
            this.data.documentKey === "HA44" ||
            this.data.documentKey === "HA48" ||
            this.data.documentKey === "HA63" ||
            this.data.documentKey === "HA62"
          ) {
            isContract = true;
          }

          const sendData = {
            data: this.form.value,
            docsSelected: this.docsSelected,
            // structureSelected: this.data.itemData ? this.data.itemData.structureSelected : undefined,
            structureSelected,
            documentFile: this.form.value.documentFile,
            contratti: isContract,
          }
          this.subscriptions.push(
            this.documentArchivesService
              .updateMultiDoc(
                this.data.documentArchiveId,
                sendData
              )
              .pipe(finalize(() => (this.formBtnOptions.active = false)))
              .subscribe(
                res => {
                  if (res.status) {
                    if (res.data && typeof res.data === "string") {
                      this.barCode = res.data.toUpperCase();
                      this.docUploaded = true;
                    }
                    this.toastrService.success(res.message);
                    // this.dialogRef.close({
                    //   save: "success"
                    // });
                  }
                },
                err => {
                  this.helperService.handleFormlyError(this.form, err);
                }
              )
          );
        } else if (this.docType && this.docType === 'paper') {
          // check contracts section
          const section = this.data.documentTypeRoute;
          if (section === 'contratti') {
            if (
              this.docsSelected && 
              Array.isArray(this.docsSelected) &&
              this.docsSelected.length > 0 &&
              (
                !this.docsSelected[0].structureSelected ||
                typeof this.docsSelected[0].structureSelected === "undefined" ||
                !Array.isArray(this.docsSelected[0].structureSelected)
              )
            ) {
              this.docsSelected[0].structureSelected = [];
            }
          }
          // First file is not required and name is equal to barcode - 2021-07-10
          if (!this.model.numFiles || !this.model.files) {
            this.model.numFiles = 1;
            this.model.files = [{
              docType: "",
              name: this.barCode
            }];
          }
          const dataToSend = {
            data: {
              ...this.model,
              property: this.data.property._id,
              code: this.barCode,
              area: this.data.selectedArea
            },
            docsSelected: this.docsSelected,
            documentFile: this.form.value.documentFile,
          }

          this.subscriptions.push(
            this.documentArchivesService
              .updateMultiPaperDoc(
                this.data.documentArchiveId,
                dataToSend
              )
              .pipe(finalize(() => (this.formBtnOptions.active = false)))
              .subscribe(
                res => {
                  if (res.status) {
                    this.docUploaded = true;
                    this.toastrService.success(res.message);
                  }
                },
                err => {
                  this.helperService.handleError(err);
                  this.helperService.handleFormlyError(this.form, err);
                }
              )
          );
        }
        
      }
    }
  }

  /**
   * Handle print file qrCode
   *
   * @since 1.0.0
   */
  onPrint(target: string): void {
    const qrCodes = [];
    if (this.model.files && this.model.files.length) {
      // If we're here we have a paper doc
      this.model.files.forEach(file => {
        if (file.name) {
          qrCodes.push(file.name);
        }
      });
    } else if (this.model.documentFile && this.model.documentFile.length) {
      // If we're here we have a digital doc
      qrCodes.push(this.barCode);
    } else {
      // Paper doc: First file is not required and name is equal to barcode - 2021-07-10
      qrCodes.push(this.barCode);
    }

    if (!qrCodes.length) {
      this.toastrService.error("No qrCodes to inject");
      return;
    }

    let data;

    if (target === "qr") {
      data = {
        target,
        qrCodes,
        propertyName: this.data.property.name,
        propertyCity: this.data.property.address.city,
        propertyAddress: `${this.data.property.address.route}, ${this.data.property.address.streetNumber}`,
        area: this.data.selectedArea,
        docs: this.docsSelected,
      };
    } else if (target === "module") {
      data = {
        target,
        qrCodes,
        propertyName: this.data.property.name,
        propertyCity: this.data.property.address.city,
        propertyAddress: `${this.data.property.address.route}, ${this.data.property.address.streetNumber}`,
        area: this.data.selectedArea,
        uploadDate: new Date(),
        content: this.model.contenutoDocumento,
        docs: this.docsSelected,
        // structures: this.data.itemData && this.data.itemData.structureSelected
        //   ? this.data.itemData.structureSelected.filter(el => el.value) : undefined,
        structures: this.structureSelected
          ? this.structureSelected.filter(el => el.value) : undefined,
      };
    } else {
      this.toastrService.error("missing print target");
    }

    // Open dialog
    this.dialog.open(PrintQrDialogComponent, {
      data,
      width: "600px"
    });
  }

  /**
   * Handle save QR code as image
   *
   * @since 1.0.0
   */
  onSaveQRAsImage(parent): void {
    // fetches base 64 date from image
    const parentElement = parent.el.nativeElement.querySelector("img").src;

    // converts base 64 encoded image to blobData
    const blobData = this.convertBase64ToBlob(parentElement);

    // saves as image
    if (window.navigator && window.navigator.msSaveOrOpenBlob) { //IE
      window.navigator.msSaveOrOpenBlob(blobData, 'Qrcode');
    } else { // chrome
      const blob = new Blob([blobData], { type: "image/png" });
      const url = window.URL.createObjectURL(blob);
      // window.open(url);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'Qrcode.png';
      link.click();
    }
  }

  /**
   * Convert image into Base64
   *
   * @since 1.0.0
   */
  private convertBase64ToBlob(Base64Image: any): Blob {
    // SPLIT INTO TWO PARTS
    const parts = Base64Image.split(';base64,');
    // HOLD THE CONTENT TYPE
    const imageType = parts[0].split(':')[1];
    // DECODE BASE64 STRING
    const decodedData = window.atob(parts[1]);
    // CREATE UNIT8ARRAY OF SIZE SAME AS ROW DATA LENGTH
    const uInt8Array = new Uint8Array(decodedData.length);
    // INSERT ALL CHARACTER CODE INTO UINT8ARRAY
    for (let i = 0; i < decodedData.length; ++i) {
      uInt8Array[i] = decodedData.charCodeAt(i);
    }
    // RETURN BLOB IMAGE AFTER CONVERSION
    return new Blob([uInt8Array], { type: imageType });
  }
}
